import { useId, useState } from 'react';
import { evaluerRobustesse } from '../lib/robustesse';

interface Props {
  /** Libellé du premier champ. */
  label?: string;
  /** Noms lus par `FormData` dans le formulaire parent. */
  name?: string;
  nameConfirmation?: string;
}

/**
 * Nouveau mot de passe, saisi deux fois, avec jauge de robustesse (12/10/2026,
 * demande de Steven sur la réinitialisation — étendu à l'inscription et au
 * changement de mot de passe pour que les trois écrans se ressemblent).
 *
 * La jauge va du rouge au vert ; les quatre règles du portail sont cochées en
 * direct sous le champ. Les champs restent NON contrôlés pour le formulaire
 * parent (il lit `FormData`) : l'état local ne sert qu'à l'affichage. Le
 * parent garde la vérification finale — règles et égalité des deux saisies —
 * au moment de l'envoi.
 */
export function NouveauMotDePasse({
  label = 'Mot de passe',
  name = 'password',
  nameConfirmation = 'confirmation',
}: Props) {
  const id = useId();
  const [valeur, setValeur] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const r = evaluerRobustesse(valeur);
  const identiques = confirmation.length > 0 && confirmation === valeur;

  return (
    <>
      <div className="field">
        <label htmlFor={name}>{label}</label>
        <input
          id={name}
          name={name}
          type="password"
          autoComplete="new-password"
          required
          aria-describedby={`${id}-jauge ${id}-regles`}
          onChange={(e) => setValeur(e.target.value)}
        />
        <div className="mdp-jauge" data-niveau={valeur ? r.niveau : 'vide'}>
          <div
            id={`${id}-jauge`}
            className="mdp-jauge-piste"
            role="meter"
            aria-label="Robustesse du mot de passe"
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={valeur ? r.niveau : 0}
            aria-valuetext={valeur ? r.libelle : 'Aucun mot de passe saisi'}
          >
            <div className="mdp-jauge-remplissage" />
          </div>
          <span className="mdp-jauge-libelle" aria-hidden="true">
            {valeur ? r.libelle : ' '}
          </span>
        </div>
        <ul id={`${id}-regles`} className="mdp-regles" aria-label="Règles du mot de passe">
          {r.criteres.map((c) => (
            <li key={c.libelle} data-ok={c.ok}>
              <span aria-hidden="true">{c.ok ? '✓' : '○'}</span> {c.libelle}
              <span className="sr-only">{c.ok ? ' : respecté' : ' : manquant'}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="field">
        <label htmlFor={nameConfirmation}>Confirmer le mot de passe</label>
        <input
          id={nameConfirmation}
          name={nameConfirmation}
          type="password"
          autoComplete="new-password"
          required
          aria-describedby={`${id}-egalite`}
          aria-invalid={confirmation.length > 0 && !identiques ? true : undefined}
          onChange={(e) => setConfirmation(e.target.value)}
        />
        <p id={`${id}-egalite`} className="mdp-egalite" data-ok={identiques} aria-live="polite">
          {confirmation.length === 0
            ? ' '
            : identiques
              ? '✓ Les deux saisies sont identiques.'
              : 'Les deux saisies ne sont pas identiques.'}
        </p>
      </div>
    </>
  );
}
